import React, { useEffect, useState } from "react";
import Page from "../../common/layout/Page";
import Loader from "../../common/Loader";
import SaveBtn from "../../common/SaveBtn";
import tools from "../../../helpers/tools";
import dataService from "../../../helpers/dataService";
import downloadTools from "../../../helpers/useDownload";
import StatutExportBadge from "./components/StatutExportBadge";

// Tant que l'export se prépare, on rafraîchit le détail à ce rythme : c'est
// la page sur laquelle atterrit le lien du mail, autant qu'elle se mette à
// jour toute seule quand le fichier devient prêt.
const INTERVALLE_RAFRAICHISSEMENT_MS = 5000;

const STATUTS_EN_PREPARATION = ["DEMANDE", "EN_COURS"];

/**
 * Page de téléchargement d'un export : seul le demandeur de l'export peut la
 * consulter et récupérer le fichier (l'API répond 404 à tout autre compte —
 * le lien reçu par mail est personnel).
 */
const ExportDetail = (props) => {
  const exportId = props.match.params.id;
  const [exportData, setExportData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [errors, setErrors] = useState(false);

  const statut = exportData ? exportData.statut : null;
  const estExpire =
    exportData &&
    (exportData.statut === "EXPIRE" ||
      (exportData.expireLe && new Date(exportData.expireLe) <= new Date()));

  useEffect(() => {
    setExportData(null);
    chargerExport(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportId]);

  // Suivi en direct pendant la préparation, coupé dès que l'export est prêt
  // (ou en échec) et au démontage du composant.
  useEffect(() => {
    if (!STATUTS_EN_PREPARATION.includes(statut)) {
      return undefined;
    }
    const intervalId = setInterval(() => {
      chargerExport(false);
    }, INTERVALLE_RAFRAICHISSEMENT_MS);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statut, exportId]);

  function chargerExport(afficherLoader) {
    if (afficherLoader) {
      setIsLoading(true);
    }
    setErrors(false);
    dataService.get(
      `exports/${exportId}`,
      (data) => setExportData(data),
      setErrors,
      () => {
        if (afficherLoader) {
          setIsLoading(false);
        }
      }
    );
  }

  function telecharger() {
    setIsDownloading(true);
    downloadTools
      .downloadFile(
        `exports/${exportId}/telechargement`,
        null,
        exportData.nomFichier,
        true,
        "GET"
      )
      .catch(() => {
        // Le fichier a pu expirer entre l'affichage et le clic : on recharge
        // le détail pour que la page raconte la vérité.
        chargerExport(false);
      })
      .finally(() => setIsDownloading(false));
  }

  return (
    <Page
      container="container-fluid px-5"
      title="Téléchargement d'un export"
      subTitle={exportData ? exportData.typeLabel : ""}
      back="/exports"
      errors={errors}
    >
      {isLoading ? (
        <Loader />
      ) : !exportData ? null : (
        <>
          <div className="d-flex align-items-center flex-wrap mb-4">
            <StatutExportBadge
              statut={exportData.statut}
              statutLabel={exportData.statutLabel}
            />
            <span className="ml-3">{exportData.typeLabel}</span>
          </div>

          <div className="row mb-3">
            <div className="col-12 col-md-4 mb-3">
              <strong>Demandé le</strong>
              <div>
                {tools.formatDate(exportData.demandeLe, "dd/MM/yyyy HH:mm")}
              </div>
            </div>
            <div className="col-12 col-md-4 mb-3">
              <strong>Disponible jusqu'au</strong>
              <div>
                {exportData.expireLe
                  ? tools.formatDate(exportData.expireLe, "dd/MM/yyyy HH:mm")
                  : "-"}
              </div>
            </div>
            <div className="col-12 col-md-4 mb-3">
              <strong>Fichier</strong>
              <div>{exportData.nomFichier || "-"}</div>
            </div>
          </div>

          {STATUTS_EN_PREPARATION.includes(exportData.statut) && (
            <div className="alert alert-info">
              Votre fichier est en cours de préparation… Vous recevrez un mail
              dès qu'il sera prêt (cette page se met à jour automatiquement).
            </div>
          )}

          {exportData.statut === "ECHEC" && (
            <div className="alert alert-danger">
              <div>
                La préparation de cet export a échoué. Vous pouvez demander un
                nouvel export depuis le suivi des exports.
              </div>
              {exportData.motifEchec && (
                <div className="mt-2">
                  <strong>Motif :</strong> {exportData.motifEchec}
                </div>
              )}
            </div>
          )}

          {estExpire && (
            <div className="alert alert-warning">
              Cet export a expiré et son fichier a été définitivement supprimé.
              Vous pouvez demander un nouvel export depuis le suivi des
              exports.
            </div>
          )}

          {exportData.statut === "TERMINE" && !estExpire && (
            <SaveBtn
              save={telecharger}
              text={
                <span>
                  <i className="fa fa-download mr-2" />
                  Télécharger le fichier (ZIP)
                </span>
              }
              type="btn-primary"
              isSaving={isDownloading}
              data-priv="download_exports"
            />
          )}
        </>
      )}
    </Page>
  );
};

export default ExportDetail;
